import React from 'react';
import ButtonIcon from '@ntuctech/devex-tangram/Button/ButtonIcon';
import ButtonOutlined from '@ntuctech/devex-tangram/Button/ButtonOutlined';
import ErrorBanner from '@ntuctech/devex-tangram/ErrorBanner';
import SvgAlert from '@ntuctech/devex-tangram/Icon/SvgAlert';
import SvgHide from '@ntuctech/devex-tangram/Icon/SvgHide';
import SvgShow from '@ntuctech/devex-tangram/Icon/SvgShow';
import Link from '@ntuctech/devex-tangram/Link';
import Typography from '@ntuctech/devex-tangram/Typography';
import differenceInDays from 'date-fns/differenceInDays';
import parseISO from 'date-fns/parseISO';
import styled from 'styled-components';

import {
  cardReplacementText,
  getCardFace,
  getCardType,
  isOcbcCard,
  parseShortHandDate,
  replaceableCardTypeMap,
} from '../../services/cards';
import { FLAGS } from '../../services/feature-flag';
import { useUser } from '../../services/profile';
import { useFeatures } from '../DynamicFeatureProvider';
import AddressForm from '../LpReplaceCard/AddressForm';
import CardAccount from '../LpReplaceCard/CardAccount';
import ProfileRow from '../ProfileRow';
import SimpleModal from '../SimpleModal';

import Finish from './Finish';

const LpPhysicalCardSc = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  :not(:last-child) {
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 1.5rem;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ProfileRowSc = styled(ProfileRow)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0.5rem 0;

  .typography-mineshaft {
    color: #666666;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LinkCardFace = styled.div`
  min-height: 7.75rem;
  min-width: 12.2875rem;
  background-image: url(${(props) => props.cardFaceLink});
  background-repeat: no-repeat;
  background-size: 100%;
  background-position: center;
  border-radius: 0.5rem;
  margin-top: 1.5rem;
`;

const CardNumSc = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  left: 0.5rem;

  @media (max-width: 767px) {
    left: 0;
  }
`;

const CardDetailSc = styled.div`
  width: 100%;
  margin-left: 2.125rem;

  @media (max-width: 767px) {
    margin: 0;
  }
`;

const StyledHelpText = styled(Typography)`
  display: flex;
  &::before {
    content: '*';
    margin-right: 0.25rem;
  }
`;

const OcbcCardBanner = (props) => {
  const { cardType, linkCardExist, handleOpenApplyCardBenefitList } = props;
  const handleLinkCardClick = () => {
    handleOpenApplyCardBenefitList(true);
  };

  return (
    <ErrorBanner
      label={
        <Typography variant="body3">
          Your {getCardType(cardType)} will be cancelled on <b>31 Jan 2023</b>. Access your Link
          Rewards membership through the{' '}
          <Link
            data-testId="fp_app--button"
            href="https://xdfz.adj.st?adj_t=mco0107"
            target="_blank"
          >
            FairPrice app
          </Link>
          &nbsp;or&nbsp;
          {linkCardExist ? (
            'Link Rewards card'
          ) : (
            <Link data-testId="link_card--button" href="#" onClick={handleLinkCardClick}>
              get a replacement card here
            </Link>
          )}
          .
        </Typography>
      }
      icon={<SvgAlert />}
      fullWidth
      attentionLevel="high"
    />
  );
};

const LpPhysicalCardsSingle = (props) => {
  const { card, handleGetPhysicalCards, linkCardExist, handleOpenApplyCardBenefitList } = props;
  const { user } = useUser();
  const [isMasked, setIsMasked] = React.useState(true);
  const [openReplaceCardModal, setOpenReplaceCardModal] = React.useState(false);
  const [openAddressModal, setOpenAddressModal] = React.useState(false);
  const [openFinishModal, setOpenFinishModal] = React.useState(false);
  const [showErrorModal, setShowErrorModal] = React.useState(false);
  const [error, setError] = React.useState({ title: '', body: '' });
  const [finishModalMsg, setFinishModalMsg] = React.useState('');
  const { isFlagEnabled } = useFeatures();

  const isNotWithinReplaceCardDate =
    differenceInDays(new Date(), parseISO(card.cardIssueDate)) <= 14;

  const parseCardNum = React.useMemo(() => card.cardNumber.match(/.{1,4}/g), [card]);
  const handleReplaceCard = () => {
    setOpenReplaceCardModal(true);
  };

  const isCardReplaceable = replaceableCardTypeMap[card.cardType];
  const cardHelpText = cardReplacementText[card.cardType];

  const isRefreshCardFaceEnabled = isFlagEnabled(FLAGS.REFRESHED_CARD_FACES);
  const cardFaceLink = getCardFace(card.cardType, isRefreshCardFaceEnabled);

  const isCardOcbcType = isOcbcCard(card.cardType);
  const isNeboCardType = card.cardType === '810300';
  const isReplaceOcbcCardEnabled = isFlagEnabled(FLAGS.OCBC_CARD_REPLACEMENT);
  const displayHelpMsg = isNeboCardType || (isCardOcbcType && !isReplaceOcbcCardEnabled);
  return (
    <LpPhysicalCardSc data-testid="linkpoints__PhysicalCards--single">
      <LinkCardFace cardFaceLink={cardFaceLink} />
      <CardDetailSc>
        <ProfileRowSc>
          <Typography variant="h5" data-testid="linkpoints__PhysicalCards__single--cardType">
            {getCardType(card.cardType)}
          </Typography>
        </ProfileRowSc>
        <ProfileRowSc>
          <Typography
            className="typography-mineshaft"
            variant="body3"
            data-testid="linkpoints__PhysicalCards__single--issuedDate"
          >
            Issued date
          </Typography>
          <Typography variant="subhead1" data-testid="linkpoints__PhysicalCards__single--dateValue">
            {parseShortHandDate(card.cardIssueDate)}
          </Typography>
        </ProfileRowSc>
        <ProfileRowSc>
          <Typography
            className="typography-mineshaft"
            variant="body3"
            data-testid="linkpoints__PhysicalCards__single--cardNum"
          >
            Card number
          </Typography>
          <CardNumSc>
            <Typography
              variant="subhead1"
              data-testid="linkpoints__PhysicalCards__single--cardNumVal"
              data-dd-action-name="Link card number"
            >
              {parseCardNum[0]} &nbsp;
              {isMasked ? '****' : parseCardNum[1]} &nbsp;
              {isMasked ? '****' : parseCardNum[2]} &nbsp;
              {parseCardNum[3]}
            </Typography>{' '}
            &nbsp;
            <ButtonIcon onClick={() => setIsMasked(!isMasked)} color="grey" compact size="s">
              {isMasked ? (
                <SvgShow color="#333333" data-testid="linkpoints__PhysicalCards__showIcon" />
              ) : (
                <SvgHide color="#333333" data-testid="linkpoints__PhysicalCards__hideIcon" />
              )}
            </ButtonIcon>
          </CardNumSc>
        </ProfileRowSc>
        {isCardReplaceable ? (
          isNotWithinReplaceCardDate ? null : (
            <ProfileRowSc>
              <ButtonOutlined
                compact
                data-testid="linkpoints__PhysicalCards__single--replaceCardBut"
                onClick={handleReplaceCard}
                disabled={isNotWithinReplaceCardDate}
              >
                Replace card
              </ButtonOutlined>
            </ProfileRowSc>
          )
        ) : (
          displayHelpMsg && (
            <StyledHelpText variant="body4" data-testid="linkpoints__PhysicalCards__helptext">
              {cardHelpText}
            </StyledHelpText>
          )
        )}
        {isCardOcbcType && isReplaceOcbcCardEnabled && (
          <OcbcCardBanner
            cardType={card.cardType}
            linkCardExist={linkCardExist}
            handleOpenApplyCardBenefitList={handleOpenApplyCardBenefitList}
          />
        )}
      </CardDetailSc>
      {openReplaceCardModal && (
        <CardAccount
          card={card}
          openCardAccModal={openReplaceCardModal}
          handleOpenCardAcc={setOpenReplaceCardModal}
          handleOpenAddressForm={setOpenAddressModal}
          cardFaceLink={cardFaceLink}
        />
      )}
      {openAddressModal && (
        <AddressForm
          initialFormData={user.residential_address}
          nameOnCard={card.nameOnCard}
          phoneNumber={user.phone_number}
          cardNumber={card.cardNumber}
          openAddressModal={openAddressModal}
          handleOpenAddressForm={setOpenAddressModal}
          handleOpenFinishModal={setOpenFinishModal}
          handleOpenError={setShowErrorModal}
          setError={setError}
          setFinishModalMsg={setFinishModalMsg}
        />
      )}
      {openFinishModal && (
        <Finish
          openFinishModal={openFinishModal}
          handleOpenFinishModal={setOpenFinishModal}
          handleGetPhysicalCards={handleGetPhysicalCards}
          finishModalMsg={finishModalMsg}
        />
      )}
      {showErrorModal && (
        <SimpleModal
          open={showErrorModal}
          title={error.title}
          body={error.body}
          ctaAction={() => setShowErrorModal(false)}
          ctaText="OK"
          testId="ReplaceCardError"
        />
      )}
    </LpPhysicalCardSc>
  );
};

export default LpPhysicalCardsSingle;
